import { useEffect, useState } from 'react';
import type { RefObject } from 'react';

type ChartPanelMeasuredSize = {
  height: number;
  width: number;
};

type UseChartPanelMeasuredSizeArgs = {
  fallbackHeight: number;
  fallbackWidth: number;
  targetRef: RefObject<HTMLElement | null>;
};

export const useChartPanelMeasuredSize = ({
  fallbackHeight,
  fallbackWidth,
  targetRef,
}: UseChartPanelMeasuredSizeArgs): ChartPanelMeasuredSize => {
  const [measuredSize, setMeasuredSize] = useState<ChartPanelMeasuredSize>({
    height: fallbackHeight,
    width: fallbackWidth,
  });

  useEffect(() => {
    const element = targetRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    let rafId = 0;

    const measure = (): void => {
      const bounds = element.getBoundingClientRect();
      const nextHeight = Math.floor(bounds.height);
      const nextWidth = Math.floor(bounds.width);

      if (
        !Number.isFinite(nextHeight) ||
        !Number.isFinite(nextWidth) ||
        nextHeight <= 0 ||
        nextWidth <= 0
      ) {
        return;
      }

      setMeasuredSize((currentSize) =>
        Math.abs(currentSize.height - nextHeight) >= 1 ||
        Math.abs(currentSize.width - nextWidth) >= 1
          ? { height: nextHeight, width: nextWidth }
          : currentSize,
      );
    };

    const schedule = (): void => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(measure);
    };

    schedule();
    const observer = new ResizeObserver(() => {
      schedule();
    });
    observer.observe(element);

    return () => {
      observer.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [targetRef]);

  useEffect(() => {
    setMeasuredSize({ height: fallbackHeight, width: fallbackWidth });
  }, [fallbackHeight, fallbackWidth]);

  return measuredSize;
};
